import { Button, Dropdown } from "antd";
import { ListVisitItem } from "../../interfaces/Visit";
import { useContext, useState } from "react";
import { LanguageContext } from "../../providers/LanguageProvider";
import { useAppDispatch } from "../../hooks";
import { changeStep, changeVisit } from "../../features/visit/visitSlice";
import { useNavigate } from "react-router-dom";
import Prescription from "./Prescription";
import Modal from "../../components/Modal";
import { getVisit } from "../../apis/visit";
import ReceiptModal from "../../components/receipts/ReceiptModal";
import PaymentModal from "../../components/PaymentModal";

type VisitContextMenuProps = {
  visit: ListVisitItem;
  onFollowUp: (visit: ListVisitItem) => void;
  onStatusChange: (visit: ListVisitItem, status: string) => void;
  onReschedule: (visit: ListVisitItem) => void;
  onPaymentSuccess?: () => void;
};

const VisitContextMenu: React.FC<VisitContextMenuProps> = ({
  visit,
  onFollowUp,
  onReschedule,
  onStatusChange,
  onPaymentSuccess,
}) => {
  const { translate: t } = useContext(LanguageContext);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isPrescriptionVisible, setIsPrescriptionVisible] = useState(false);
  const [fullVisit, setFullVisit] = useState<any>(null);
  const [isReceiptVisible, setIsReceiptVisible] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState<{ total: number; discount: number; amountPaid: number; amountDue: number } | null>(null);

  const openPrescription = () => {
    setIsPrescriptionVisible(true);
    getVisit(visit.id).then((res: any) => setFullVisit(res)).catch(() => {});
  };

  const handleMakePayment = () => {
    const inv = visit.invoice;
    setPaymentSummary({
      total: inv?.totalAmount ?? 0,
      discount: 0,
      amountPaid: inv?.paidAmount ?? 0,
      amountDue: (inv?.totalAmount ?? 0) - (inv?.paidAmount ?? 0),
    });
    setIsPaymentModalOpen(true);
  };

  const handleEdit = (visit: ListVisitItem) => {
    const editableVisit = { ...visit, symptoms: [], visitFiles: [] };
    dispatch(changeStep(1));
    dispatch(changeVisit(editableVisit));
    console.log("visit", visit);
    const visitType = visit.clinicVisitType.visitType.type?.toLowerCase();
    console.log({ visitType });
    if (visitType === "opd") {
      navigate(`/opd/visits/${visit.id}`);
    }
    else if (visitType === "ipd") {
      navigate(`/ipd/visits/${visit.id}`);
    }
    else {
      navigate(`/visits/${visit.id}`);
    }
  };

  return (
    <div>
      <Dropdown
        menu={{
          items: [
            {
              key: "edit",
              label: (
                <span>
                  <button onClick={() => handleEdit(visit)}>
                    {/* {t("visit_default_action")} */}
                    {t("edit")}
                  </button>
                </span>
              ),
              disabled:
                visit.visitStatus !== "completed" &&
                visit.visitStatus !== "expired" &&
                visit.visitStatus !== "cancelled"
                  ? false
                  : true,
            },

            {
              key: "view",
              label: (
                <span>
                  <a
                    href={`/view/visits/${visit.id}`}
                    rel="noopener noreferrer"
                  >
                    {t("view")}
                  </a>
                </span>
              ),
              disabled: visit.visitStatus === "completed" ? false : true,
            },
            {
              key: "follow_up",
              label: (
                <span>
                  <button
                    onClick={() => onFollowUp(visit)}
                    disabled={visit.visitStatus !== "completed"}
                  >
                    {t("create_follow_up_visit")}
                  </button>
                </span>
              ),
              disabled: visit.visitStatus === "completed" ? false : true,
            },

            {
              key: "reschedule",
              label: (
                <span>
                  <button
                    className="capitalize"
                    onClick={() => onReschedule(visit)}
                  >
                    {t("reschedule")}
                  </button>
                </span>
              ),
              disabled: visit.visitStatus !== "scheduled",
            },

            {
              key: "prescription",
              label: (
                <span onClick={openPrescription}>
                  {t("view_prescription")}
                </span>
              ),
              disabled: visit.visitStatus === "completed" ? false : true,
            },
            {
              key: "receipt",
              label: (
                <span onClick={() => setIsReceiptVisible(true)}>
                  View Receipt
                </span>
              ),
              disabled: visit.invoice?.status !== "paid" && visit.invoice?.status !== "partial",
            },
            {
              key: "make_payment",
              label: (
                <span onClick={handleMakePayment}>
                  Make Payment
                </span>
              ),
              disabled: !visit.invoice || visit.invoice?.status === "paid",
            },
            {
              key: "transactions",
              label: (
                <span>
                  <button
                    onClick={() => navigate(`/transactions/visits/${visit.id}`)}
                  >
                    {t("transactions")}
                  </button>
                </span>
              ),
            },

            {
              key: "status",
              label: (
                <span> {t("change_status")}</span>),
              children: [
                {
                  key: "checked_in",
                  label: (
                    <span>
                      <button
                        onClick={() => onStatusChange(visit.id, "checked_in")}
                        disabled={visit.visitStatus !== "scheduled"}
                      >
                        {t("checked_in")}
                      </button>
                    </span>
                  ),
                  disabled: visit.visitStatus === "scheduled" ? false : true,
                },

                {
                  key: "completed",
                  label: (
                    <span>
                      <button
                        onClick={() => onStatusChange(visit.id, "completed")}
                        disabled={
                          visit.visitStatus === "completed" ||
                          visit.visitStatus === "expired" ||
                          visit.visitStatus === "cancelled"
                        }
                      >
                        {t("completed")}
                      </button>
                    </span>
                  ),
                  disabled:
                    visit.visitStatus === "completed" ||
                    visit.visitStatus === "expired" ||
                    visit.visitStatus === "cancelled"
                      ? true
                      : false,
                },
                {
                  key: "cancelled",
                  label: (
                    <span>
                      <button
                        onClick={() => onStatusChange(visit.id, "cancelled")}
                        disabled={visit.visitStatus !== "scheduled"}
                      >
                        {t("cancelled")}
                      </button>
                    </span>
                  ),
                  disabled: visit.visitStatus === "scheduled" ? false : true,
                },
              ],
            },
          ],
        }}
        placement="bottomLeft"
      >
        <Button className="capitalize">{t("actions")}</Button>    
      </Dropdown>
      {isPrescriptionVisible && (
        <Modal
          width="max-w-3xl"
          title="Prescription Preview"
          onClose={() => { setIsPrescriptionVisible(false); setFullVisit(null); }}
          modalContent={
            fullVisit ? (
              <Prescription
                preview
                prescriptionDetails={{
                  symptoms: (fullVisit.symptoms ?? []).map((s: any) => s.label ?? s),
                  medicines: fullVisit.medicines ?? [],
                  finalDiagnosis: (fullVisit.finalDiagnosis ?? []).map((d: any) => d.label).join(", ") || null,
                  provisionalDiagnosis: (fullVisit.provisionalDiagnosis ?? []).map((d: any) => d.label).join(", ") || null,
                  advice: fullVisit.advice ?? "",
                  patient: fullVisit.patient ?? visit.patient,
                  visitId: visit.id,
                  vitals: fullVisit.meta?.vitals ?? [],
                }}
              />
            ) : (
              <div className="flex justify-center py-10 text-gray-400">Loading prescription...</div>
            )
          }
        />
      )}
      {isReceiptVisible && (visit.invoice?.status === "paid" || visit.invoice?.status === "partial") && (
        <ReceiptModal
          isOpen={isReceiptVisible}
          onClose={() => setIsReceiptVisible(false)}
          invoiceId={visit.invoice?.id ?? null}
          receiptType="opd"
        />
      )}
      {isPaymentModalOpen && paymentSummary && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => { setIsPaymentModalOpen(false); setPaymentSummary(null); }}
          total={paymentSummary.total}
          discount={paymentSummary.discount}
          amountAlreadyPaid={paymentSummary.amountPaid}
          allowPartialOrUnpaid={true}
          patientId={visit.patient?.id}
          patientName={visit.patient ? `${visit.patient.firstName} ${visit.patient.lastName}` : undefined}
          sourceType="visit"
          sourceId={visit.id}
          onPaymentSuccess={() => {
            setIsPaymentModalOpen(false);
            setPaymentSummary(null);
            onPaymentSuccess?.();
          }}
        />
      )}
    </div>
  );
};

export default VisitContextMenu;
